import { Button, Chip, Grid, Icon, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import { useState } from 'react';
import { AppCard } from '../ui';

interface IFerramentasDaListagemProps {
    mostrarBotaoFecharConta?: boolean;
    mostrarBotaoAbrirConta?: boolean;
    aoClicarEmPesquisa?: (
        datainicial: Dayjs | null,
        datafinal: Dayjs | null,
    ) => void;
    aoClicarEmImpressao?: () => void;
    aoClicarEmFecharConta?: () => void;
    aoClicarEmAbrirConta?: () => void;
    // Só faz sentido pra contas 'recebiveis' — liquidar com a operadora da
    // maquininha (ver DialogTransferirRecebivel).
    mostrarBotaoTransferirRecebivel?: boolean;
    aoClicarEmTransferirRecebivel?: () => void;
    // Faz sentido pra contas 'banco' e 'cofre' — transferência pra uma
    // conta banco ou pra um caixa físico, nunca pra recebíveis (ver
    // DialogTransferenciaConta). O rótulo dos itens do menu muda conforme a
    // origem (ex.: "Saque Bancário" só faz sentido saindo do banco).
    mostrarBotaoTransferencia?: boolean;
    tipoContaOrigemTransferencia?: 'banco' | 'cofre';
    aoClicarEmTransferenciaParaConta?: () => void;
    aoClicarEmTransferenciaParaCaixa?: () => void;
    // Seleção de conta agora acontece pela sidebar (ver SidebarContasConciliacao)
    // — este rótulo só exibe qual conta está selecionada no momento.
    contaSelecionadaLabel?: string;
    // Só preenchido quando a conta selecionada é tipo='caixa' — sem os
    // botões de Abrir/Fechar Sessão (removidos daqui: só quem está com o
    // caixa aberto pode mexer nele), esse chip é a única pista de que uma
    // transferência pra esse caixa vai ser recusada por falta de sessão.
    statusSessaoCaixa?: 'aberta' | 'fechada';
    // Abre o dialog de detalhamento de saldo por conta (recebíveis, contas
    // bancárias, caixas e cofre individualmente) — não depende de conta
    // selecionada, sempre visível.
    aoClicarEmDetalharSaldos?: () => void;
}

// Conta selecionada, datas, Filtrar e os botões de ação ficam todos numa
// linha só a partir de md — o Chip da conta é compacto de propósito (só o
// nome, sem legenda "Conta selecionada" acima) pra sobrar espaço horizontal
// pros botões não quebrarem linha.
export const FerramentasDaListagemFinanceiroConciliacao: React.FC<
    IFerramentasDaListagemProps
> = ({
    aoClicarEmPesquisa,
    aoClicarEmImpressao,
    aoClicarEmFecharConta,
    aoClicarEmAbrirConta,
    aoClicarEmTransferirRecebivel,
    aoClicarEmTransferenciaParaConta,
    aoClicarEmTransferenciaParaCaixa,
    aoClicarEmDetalharSaldos,
    mostrarBotaoFecharConta = false,
    mostrarBotaoAbrirConta = false,
    mostrarBotaoTransferirRecebivel = false,
    mostrarBotaoTransferencia = false,
    tipoContaOrigemTransferencia = 'banco',
    contaSelecionadaLabel = '',
    statusSessaoCaixa,
}) => {
    const [dataInicial, setDataInicial] = useState<Dayjs | null>(dayjs());
    const [dataFinal, setDataFinal] = useState<Dayjs | null>(dayjs());
    const [anchorTransferencia, setAnchorTransferencia] = useState<HTMLElement | null>(null);

    const handlePesquisa = () => {
        aoClicarEmPesquisa?.(dataInicial, dataFinal);
    };

    const labelTransferenciaParaConta = tipoContaOrigemTransferencia === 'cofre'
        ? 'Transferência para Conta Bancária'
        : 'Transferência entre Contas';
    const labelTransferenciaParaCaixa = tipoContaOrigemTransferencia === 'cofre'
        ? 'Transferência para Caixa'
        : 'Saque Bancário';

    return (
        <AppCard sx={{ mx: { xs: 1, sm: 2 }, p: { xs: 1, sm: 1.25 } }}>
            <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm="auto" sx={{ minWidth: 0, maxWidth: { sm: 200 } }}>
                    <Tooltip title={contaSelecionadaLabel || 'Nenhuma conta selecionada'}>
                        <Chip
                            label={contaSelecionadaLabel || 'Nenhuma conta selecionada'}
                            color={contaSelecionadaLabel ? 'primary' : 'default'}
                            variant="outlined"
                            sx={{ maxWidth: '100%', fontWeight: 700 }}
                        />
                    </Tooltip>
                </Grid>
                {statusSessaoCaixa && (
                    <Grid item xs="auto">
                        <Tooltip title={statusSessaoCaixa === 'fechada' ? 'Nenhuma movimentação pode ser feita nesse caixa até a sessão ser aberta' : 'Esse caixa está com sessão aberta'}>
                            <Chip
                                label={statusSessaoCaixa === 'aberta' ? 'Sessão aberta' : 'Sessão fechada'}
                                color={statusSessaoCaixa === 'aberta' ? 'success' : 'default'}
                                size="small"
                            />
                        </Tooltip>
                    </Grid>
                )}
                <Grid item xs={6} sm="auto" sx={{ width: { sm: 150 } }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Data inicial"
                            format="DD/MM/YYYY"
                            value={dataInicial}
                            onChange={(newValue) => setDataInicial(newValue)}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={6} sm="auto" sx={{ width: { sm: 150 } }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Data final"
                            format="DD/MM/YYYY"
                            value={dataFinal}
                            onChange={(newValue) => setDataFinal(newValue)}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs="auto">
                    <IconButton color="primary" onClick={handlePesquisa} title="Pesquisar">
                        <SearchIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                </Grid>
                <Grid item xs={6} sm="auto">
                    <Button
                        fullWidth
                        size="small"
                        color="primary"
                        variant="outlined"
                        disableElevation
                        onClick={aoClicarEmImpressao}
                        startIcon={<Icon>print_outlined</Icon>}
                    >
                        Imprimir
                    </Button>
                </Grid>
                <Grid item xs={6} sm="auto">
                    <Button
                        fullWidth
                        size="small"
                        color="primary"
                        variant="outlined"
                        disableElevation
                        onClick={aoClicarEmDetalharSaldos}
                        startIcon={<Icon>account_balance</Icon>}
                    >
                        Detalhar Saldos
                    </Button>
                </Grid>
                {mostrarBotaoTransferirRecebivel && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="success"
                            variant="contained"
                            disableElevation
                            onClick={aoClicarEmTransferirRecebivel}
                            startIcon={<Icon>sync_alt</Icon>}
                        >
                            Transferir
                        </Button>
                    </Grid>
                )}
                {mostrarBotaoTransferencia && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="success"
                            variant="contained"
                            disableElevation
                            onClick={(e) => setAnchorTransferencia(e.currentTarget)}
                            startIcon={<Icon>sync_alt</Icon>}
                        >
                            Transferência
                        </Button>
                        <Menu
                            anchorEl={anchorTransferencia}
                            open={!!anchorTransferencia}
                            onClose={() => setAnchorTransferencia(null)}
                        >
                            <MenuItem onClick={() => { setAnchorTransferencia(null); aoClicarEmTransferenciaParaConta?.(); }}>
                                {labelTransferenciaParaConta}
                            </MenuItem>
                            <MenuItem onClick={() => { setAnchorTransferencia(null); aoClicarEmTransferenciaParaCaixa?.(); }}>
                                {labelTransferenciaParaCaixa}
                            </MenuItem>
                        </Menu>
                    </Grid>
                )}
                {mostrarBotaoFecharConta && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="primary"
                            variant="contained"
                            disableElevation
                            onClick={aoClicarEmFecharConta}
                            startIcon={<Icon>lock</Icon>}
                        >
                            Fechar Conta
                        </Button>
                    </Grid>
                )}
                {mostrarBotaoAbrirConta && (
                    <Grid item xs={6} sm="auto">
                        <Button
                            fullWidth
                            size="small"
                            color="primary"
                            variant="contained"
                            disableElevation
                            onClick={aoClicarEmAbrirConta}
                            startIcon={<Icon>lock_open</Icon>}
                        >
                            Abrir Conta
                        </Button>
                    </Grid>
                )}
            </Grid>
        </AppCard>
    );
};
